export interface AppSettings {
  isDarkMode: boolean;
  showWaveBackground: boolean;
  backgroundType: 'solid' | 'image';
}

export interface AuthState {
  dailyMessageCount: number;
  totalMessageCount: number;
  lastMessageDate?: string; // To track daily reset
}

// Maximum number of messages to keep per session
const MAX_MESSAGES_PER_SESSION = 50;
// Maximum number of sessions to keep
const MAX_SESSIONS = 20;
// Maximum length of message content to store
const MAX_MESSAGE_LENGTH = 2000;

export interface ChatMessage {
  id: string | number;
  role: 'user' | 'assistant' | 'system' | 'ai';
  content: string;
  timestamp: string | Date;
  type?: 'user' | 'ai';
  model?: string;
  isImage?: boolean;
  attachedMedia?: any;
  selectedAgent?: any;
  agentData?: any;
}

export interface ChatSession {
  id: string;
  title: string;
  messages: ChatMessage[];
  lastMessage: string;
  messageCount: number;
  model: string;
  date: string;
  timestamp: string | Date;
  createdAt?: string;
  updatedAt?: string;
}

interface StorageData {
  app: AppSettings;
  auth: AuthState;
  chats: {
    activeSessionId: string | null;
    sessions: Record<string, ChatSession>;
  };
}

import { LEGACY_STORAGE_KEYS, LOCAL_STORAGE_KEYS, STORAGE_FIELD_KEYS, STORAGE_VERSION } from './storageKeys';

const STORAGE_KEY = LOCAL_STORAGE_KEYS.appRoot;
const STORAGE_VERSION_KEY = `${LOCAL_STORAGE_KEYS.appRoot}_version`;

const DEFAULT_STATE: StorageData = {
  app: {
    isDarkMode: true,
    showWaveBackground: true,
    backgroundType: 'solid',
  },
  auth: {
    dailyMessageCount: 0,
    totalMessageCount: 0,
  },
  chats: {
    activeSessionId: null,
    sessions: {},
  },
};

const transformLegacyStorage = (raw: any): StorageData => {
  const app = raw.app || {};
  const auth = raw.auth || {};
  const chats = raw.chats || {};

  return {
    app: {
      ...DEFAULT_STATE.app,
      ...app,
    },
    auth: {
      ...DEFAULT_STATE.auth,
      dailyMessageCount: auth.dailyMessageCount ?? DEFAULT_STATE.auth.dailyMessageCount,
      totalMessageCount: auth.totalMessageCount ?? DEFAULT_STATE.auth.totalMessageCount,
      lastMessageDate: auth.lastMessageDate,
    },
    chats: {
      activeSessionId: chats.activeSessionId ?? null,
      sessions: chats.sessions ?? {},
    },
  };
};

const transformHashedStorage = (raw: any): StorageData => {
  const app = raw?.[STORAGE_FIELD_KEYS.app] || {};
  const auth = raw?.[STORAGE_FIELD_KEYS.auth] || {};
  const chats = raw?.[STORAGE_FIELD_KEYS.chats] || {};

  return {
    app: {
      ...DEFAULT_STATE.app,
      ...app,
    },
    auth: {
      ...DEFAULT_STATE.auth,
      dailyMessageCount: auth[STORAGE_FIELD_KEYS.dailyMessageCount] ?? DEFAULT_STATE.auth.dailyMessageCount,
      totalMessageCount: auth[STORAGE_FIELD_KEYS.totalMessageCount] ?? DEFAULT_STATE.auth.totalMessageCount,
      lastMessageDate: auth[STORAGE_FIELD_KEYS.lastMessageDate],
    },
    chats: {
      activeSessionId: chats[STORAGE_FIELD_KEYS.activeSessionId] ?? null,
      sessions: chats[STORAGE_FIELD_KEYS.sessions] ?? {},
    },
  };
};

class StorageManager {
  private static instance: StorageManager;
  private data: StorageData;

  private constructor() {
    this.data = this.loadData();
    this.cleanupOldSessions();
  }

  public static getInstance(): StorageManager {
    if (!StorageManager.instance) {
      StorageManager.instance = new StorageManager();
    }
    return StorageManager.instance;
  }

  private loadData(): StorageData {
    try {
      // Prefer hashed storage key; fall back to legacy root key if present
      let source: string | null = localStorage.getItem(STORAGE_KEY);
      if (!source) {
        source = localStorage.getItem(LEGACY_STORAGE_KEYS.appRoot as any);
      }
      if (!source) return { ...DEFAULT_STATE };

      const parsed = JSON.parse(source);
      // Detect format by presence of hashed field keys
      const looksHashed = parsed && (
        parsed.hasOwnProperty(STORAGE_FIELD_KEYS.app) ||
        parsed.hasOwnProperty(STORAGE_FIELD_KEYS.auth) ||
        parsed.hasOwnProperty(STORAGE_FIELD_KEYS.chats)
      );

      let data: StorageData = looksHashed
        ? transformHashedStorage(parsed)
        : transformLegacyStorage(parsed);

      // Normalize legacy background types (map 'wave' and 'grid' to 'solid')
      const bt: any = data.app.backgroundType;
      if (bt === 'wave' || bt === 'grid' || bt == null) {
        data.app.backgroundType = 'solid';
      }

      // Apply daily reset logic
      if (this.isNewDay(data.auth.lastMessageDate)) {
        data.auth.dailyMessageCount = 0;
        data.auth.lastMessageDate = new Date().toISOString().split('T')[0];
      }

      return data;
    } catch (error) {
      console.error('Failed to load storage data:', error);
      return { ...DEFAULT_STATE };
    }
  }

  private saveData() {
    try {
      // Persist with hashed field names
      const serialized = {
        [STORAGE_FIELD_KEYS.app]: {
          ...this.data.app,
        },
        [STORAGE_FIELD_KEYS.auth]: {
          [STORAGE_FIELD_KEYS.dailyMessageCount]: this.data.auth.dailyMessageCount,
          [STORAGE_FIELD_KEYS.totalMessageCount]: this.data.auth.totalMessageCount,
          [STORAGE_FIELD_KEYS.lastMessageDate]: this.data.auth.lastMessageDate,
        },
        [STORAGE_FIELD_KEYS.chats]: {
          [STORAGE_FIELD_KEYS.activeSessionId]: this.data.chats.activeSessionId,
          [STORAGE_FIELD_KEYS.sessions]: this.data.chats.sessions,
        },
      } as any;

      localStorage.setItem(STORAGE_KEY, JSON.stringify(serialized));
      return true;
    } catch (error) {
      console.error('Failed to save storage data:', error);
      return false;
    }
  }

  private isNewDay(lastDate?: string): boolean {
    if (!lastDate) return true;
    const today = new Date().toISOString().split('T')[0];
    return lastDate !== today;
  }

  private cleanupOldSessions() {
    const sessions = Object.values(this.data.chats.sessions);
    if (sessions.length <= MAX_SESSIONS) return;

    // Sort sessions by last updated date (newest first)
    const sortedSessions = [...sessions].sort((a, b) =>
      new Date(b.updatedAt || b.timestamp).getTime() - new Date(a.updatedAt || a.timestamp).getTime()
    );

    // Keep only the most recent sessions
    const sessionsToKeep = sortedSessions.slice(0, MAX_SESSIONS);
    const sessionsToDelete = sortedSessions.slice(MAX_SESSIONS);

    // Delete old sessions
    sessionsToDelete.forEach(session => {
      delete this.data.chats.sessions[session.id];
    });

    this.saveData();
  }

  // App Settings
  getAppSettings(): AppSettings {
    return { ...this.data.app };
  }

  updateAppSettings(settings: Partial<AppSettings>) {
    this.data.app = { ...this.data.app, ...settings };
    return this.saveData();
  }

  // Auth State
  getAuthState(): AuthState {
    return { ...this.data.auth };
  }

  updateAuthState(state: Partial<AuthState>) {
    this.data.auth = { ...this.data.auth, ...state };
    return this.saveData();
  }

  incrementMessageCount() {
    const today = new Date().toISOString().split('T')[0];
    if (this.data.auth.lastMessageDate !== today) {
      this.data.auth.dailyMessageCount = 0;
      this.data.auth.lastMessageDate = today;
    }
    this.data.auth.dailyMessageCount++;
    this.data.auth.totalMessageCount++;

    // Save the updated count
    this.saveData();

    // Dispatch a custom event to notify about the message count change
    if (typeof window !== 'undefined') {
      // Update localStorage to trigger storage event in other tabs
      localStorage.setItem(LOCAL_STORAGE_KEYS.dailyMessageCount, this.data.auth.dailyMessageCount.toString());

      // Dispatch a custom event for the current tab
      window.dispatchEvent(new CustomEvent('messageCountChanged'));
    }

    return this.data.auth.dailyMessageCount;
  }

  // Chat Sessions
  getActiveSession(): ChatSession | null {
    if (!this.data.chats.activeSessionId) return null;
    return this.getSession(this.data.chats.activeSessionId);
  }

  // Explicitly set the active session id (or clear it when null)
  setActiveSession(sessionId: string | null) {
    this.data.chats.activeSessionId = sessionId;
    return this.saveData();
  }

  getSession(sessionId: string): ChatSession | null {
    const session = this.data.chats.sessions[sessionId];
    return session ? { ...session } : null;
  }

  getAllSessions(): ChatSession[] {
    return Object.values(this.data.chats.sessions).sort((a, b) =>
      new Date(b.updatedAt || b.timestamp).getTime() - new Date(a.updatedAt || a.timestamp).getTime()
    );
  }

  async createSession(title: string = 'New Chat'): Promise<ChatSession> {
    const sessionId = Date.now().toString();
    const now = new Date().toISOString();

    // Import the title generator dynamically to avoid circular dependencies
    let generatedTitle = title;
    try {
      // Only generate AI title if we have a meaningful message (not just "New Chat")
      if (title !== 'New Chat' && title.length > 5) {
        const { generateChatTitle } = await import('../services/titleGeneratorService');
        generatedTitle = await generateChatTitle(title);
      } else {
        generatedTitle = '💬 New Chat';
      }
    } catch (error) {
      generatedTitle = title.length > 50 ? `💬 ${title.slice(0, 30)}...` : `💬 ${title}`;
    }

    const newSession: ChatSession = {
      id: sessionId,
      title: generatedTitle,
      messages: [],
      lastMessage: '',
      messageCount: 0,
      model: 'deepseek',
      date: new Date().toLocaleDateString(),
      timestamp: now,
      createdAt: now,
      updatedAt: now
    };

    this.data.chats.sessions[sessionId] = newSession;
    this.data.chats.activeSessionId = sessionId;
    this.saveData();

    return { ...newSession }; // Return a new object to prevent reference issues
  }

  updateSession(sessionId: string, updates: Partial<ChatSession>) {
    const session = this.data.chats.sessions[sessionId];
    if (!session) return false;

    this.data.chats.sessions[sessionId] = {
      ...session,
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    return this.saveData();
  }

  deleteSession(sessionId: string) {
    if (!this.data.chats.sessions[sessionId]) return false;

    delete this.data.chats.sessions[sessionId];

    if (this.data.chats.activeSessionId === sessionId) {
      this.data.chats.activeSessionId = null;
    }

    return this.saveData();
  }

  // Clean up messages in a session
  private cleanupSessionMessages(sessionId: string) {
    const session = this.data.chats.sessions[sessionId];
    if (!session || !session.messages) return;

    // Keep only the most recent messages
    if (session.messages.length > MAX_MESSAGES_PER_SESSION) {
      const messagesToKeep = session.messages.slice(-MAX_MESSAGES_PER_SESSION);
      session.messages = messagesToKeep;
      session.messageCount = messagesToKeep.length;
      if (messagesToKeep.length > 0) {
        session.lastMessage = messagesToKeep[messagesToKeep.length - 1].content.substring(0, 100);
      }
      session.updatedAt = new Date().toISOString();
      this.saveData();
    }
  }


  // Compress message content to save space
  private compressMessage(message: string): string {
    if (!message) return '';

    // If it looks like a JSON payload (starts with { and ends with }), skip compression
    // as it likely contains rich metadata that should not be truncated or whitespace-stripped
    const trimmed = message.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      return message;
    }

    // Simple compression - remove extra whitespace and newlines
    return message
      .replace(/\s+/g, ' ')
      .replace(/\s+([.,!?])/g, '$1')
      .trim()
      .substring(0, MAX_MESSAGE_LENGTH);
  }

  addMessage(sessionId: string, message: Omit<ChatMessage, 'id' | 'timestamp'>) {
    // Clean up old sessions first
    this.cleanupOldSessions();

    const session = this.data.chats.sessions[sessionId];
    if (!session) return false;

    // Compress message content
    const compressedContent = this.compressMessage(message.content);

    const newMessage: ChatMessage = {
      ...message,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      content: compressedContent
    };

    // Initialize messages array if it doesn't exist
    if (!session.messages) {
      session.messages = [];
    }

    session.messages.push(newMessage);
    session.lastMessage = compressedContent.substring(0, 100);
    session.messageCount = session.messages.length;
    session.updatedAt = new Date().toISOString();

    // Clean up messages in this session
    this.cleanupSessionMessages(sessionId);
    return this.saveData();
  }

  // Storage Management
  getStorageUsage(): { used: number; total: number; percentage: number } {
    try {
      const total = 5 * 1024 * 1024; // 5MB is typical localStorage limit
      let used = 0;

      // Calculate used space
      Object.keys(localStorage).forEach(key => {
        const amount = (localStorage[key].length * 2) / 1024 / 1024; // in MB
        used += amount;
      });

      return {
        used: parseFloat(used.toFixed(2)),
        total: 5, // MB
        percentage: Math.min(100, Math.round((used / 5) * 100)),
      };
    } catch (error) {
      console.error('Error calculating storage usage:', error);
      return { used: 0, total: 5, percentage: 0 };
    }
  }

  clearAllData() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      this.data = { ...DEFAULT_STATE };
      return true;
    } catch (error) {
      console.error('Failed to clear storage:', error);
      return false;
    }
  }

  clearChats() {
    try {
      this.data.chats = {
        activeSessionId: null,
        sessions: {},
      };
      return this.saveData();
    } catch (error) {
      console.error('Failed to clear chats:', error);
      return false;
    }
  }
}

// Create the storage manager instance
const storageManager = StorageManager.getInstance();

// Create a wrapper object that combines StorageManager and localStorage methods
const storage = {
  // StorageManager methods
  getAppSettings: () => storageManager.getAppSettings(),
  updateAppSettings: (settings: Partial<AppSettings>) => storageManager.updateAppSettings(settings),
  getAuthState: () => storageManager.getAuthState(),
  updateAuthState: (state: Partial<AuthState>) => storageManager.updateAuthState(state),
  incrementMessageCount: () => storageManager.incrementMessageCount(),
  getActiveSession: () => storageManager.getActiveSession(),
  setActiveSession: (sessionId: string | null) => storageManager.setActiveSession(sessionId),
  getSession: (sessionId: string) => storageManager.getSession(sessionId),
  getAllSessions: () => storageManager.getAllSessions(),
  createSession: (title?: string) => storageManager.createSession(title),
  updateSession: (sessionId: string, updates: Partial<ChatSession>) =>
    storageManager.updateSession(sessionId, updates),
  deleteSession: (sessionId: string) => storageManager.deleteSession(sessionId),
  addMessage: (sessionId: string, message: Omit<ChatMessage, 'id' | 'timestamp'>) =>
    storageManager.addMessage(sessionId, message),
  getStorageUsage: () => storageManager.getStorageUsage(),
  clearAllData: () => storageManager.clearAllData(),
  clearChats: () => storageManager.clearChats(),

  // localStorage compatible methods
  getItem: (key: string): string | null => {
    if (key === 'chatHistory') {
      const sessions = storageManager.getAllSessions();
      return JSON.stringify(sessions);
    }
    return localStorage.getItem(key);
  },

  setItem: (key: string, value: string): void => {
    if (key === 'chatHistory') {
      try {
        const sessions = JSON.parse(value) as ChatSession[];
        storageManager.clearChats();
        sessions.forEach(session => {
          storageManager.updateSession(session.id, session);
        });
      } catch (error) {
        console.error('Failed to parse chat history:', error);
      }
    } else {
      localStorage.setItem(key, value);
    }
  },

  removeItem: (key: string): void => {
    if (key === 'chatHistory') {
      storageManager.clearChats();
    } else {
      localStorage.removeItem(key);
    }
  },

  clear: (): void => {
    storageManager.clearAllData();
    localStorage.clear();
  }
};

// Export both the storage object and the StorageManager class
export { storage, StorageManager };
